/**
 * **打印包的示例文档** —— 「文件 → 示例文件」里那一份：一张 60×40mm 的
 * **幸运饼干签**。
 *
 * ## 为什么示例要做成"一张真的能打的小标签"
 *
 * 用户原话：
 *
 * > 再给我一个示例文档（娱乐，域节点包可以带示例文档，被自动加载到
 * > 文件菜单下的示例文件菜单下）
 *
 * 于是「这个包能干什么」有了一个**打开就能看的答案**。判据是**一眼看懂**：
 * 所以不是"把七种节点各摆一遍"的目录图（那种图能证明节点存在，但看不出
 * 它们能一起干什么），而是一张真的有内容、真的有排版的小标签 ——
 * 标题、一句运势、一个二维码，外加一个「运行」时才掷出来的幸运数字。
 *
 * ## 这张图上演示了三件事
 *
 * | 演示什么 | 落在哪 |
 * |---|---|
 * | **一条内容源喂两处** | `const.text` → 文本组件**和**二维码（改一处，两处一起变） |
 * | **运行期才算的值** | `core.js` 掷幸运数字；设计期不出值，下游文本组件回退到内联占位值 |
 * | **文档 / 页面 / 组件三层各管一段** | 文档（纸张 + 作业）→ 页面（收了哪些组件）→ 组件（几何 mm） |
 *
 * ## 四条硬规矩（`verify:print` 逐条钉着）
 *
 * ① **行一律从类型定义深拷贝**（`cloneRows`）。拿到共享的 `def.rows` 直接改
 *    会污染整个目录：改一个实例、画布上所有同类型节点跟着变（这个坑
 *    仓库里记过两次）。
 * ② **控件值真的写进去**：只把节点摆出来、不填值是"看着有节点、打开是空的"。
 * ③ **设计期求值不许有 error**：示例是用户看到的第一屏，红着出去等于劝退。
 *    所以 `core.js` 只当**额外的**内容源 —— "打开就有东西可看"这件事
 *    不能指望它（它设计期给的是空值）。
 * ④ **`graph()` 每次都给新的一份**：模型是可变的（用户点开示例之后就会改它），
 *    共享一份的话"改一次示例、菜单里的示例全变"。
 *
 * ## ⚠️ 为什么这里能 import 内核的节点定义
 *
 * 示例里用了 `const.text` 和 `core.js`（内核包的节点），而它们的**行必须
 * 从真实定义拷贝**（手写一份行 = 第二份真相：内核改了行 id 之后，
 * 示例的连线会**静默**接不上 —— 画面上是一条悬空的线）。
 *
 * 这条依赖是安全的：内核包（`kind: 'core'`）**强制加载、不可卸载**，
 * 所以「包之间不许互相 import 具体节点」那条规矩想防的
 * "可选加载变成必须一起装"在这里根本不成立。
 *
 * ⚠️ 但**域包之间**仍然不许这么干：打印包不许 import 函数包的节点定义 ——
 * 那种依赖会让"只装打印包"变成一个装不起来的组合。
 */
import { CONST_NODE_TYPES } from '@/node/constNodes'
import { JS_NODE_TYPE, rowsForJsNode, type JsPortsConfig } from '@/node/jsNodeType'
/* ⚠️ 模板是共享的、实例必须是自己的 —— 深拷贝只有这一处实现（见 `cloneRows`） */
import { cloneRows } from '@/node/rows'
import {
  BOX_NODE_TYPE,
  DOCUMENT_NODE_TYPE,
  PAGE_NODE_TYPE,
  PRINT_NODE_TYPES,
  QRCODE_NODE_TYPE,
  TEXT_NODE_TYPE,
} from './nodes'
import type { NodeTypeDef, PackageExample } from '@/node/package'
import type { GraphNodeModel, NodeRow, SubGraph, SubGraphEdge } from '@/types/node'

/* ══════════════════════════════════════════════════════════════════
   内容（文案与那段 JS）
   ══════════════════════════════════════════════════════════════════ */

/**
 * 那句祝福 —— **文本组件和二维码共用它**（一处改、两处一起变）。
 *
 * ⚠️ 换行是故意的：标签宽 60mm，两行比一行好读，也短到二维码装得下
 * （22 个字含标点 → 33×33 模块，加两侧静区是 41 模块；见下面几何那段）。
 */
const FORTUNE = '今天会遇到一件小事，\n正好是你一直想做的。'

/**
 * 幸运数字在**设计期**的占位值。
 *
 * ⚠️ 它是"打开就有东西看"的保证：算幸运数字的那个 JS 节点设计期
 * **不出值**（`jsDesignTime` 给的是空对象），下游文本组件于是回退到
 * 这一份内联值。所以这张标签不依赖"先点一次运行"才好看。
 */
const LUCKY_PLACEHOLDER = '幸运数字 7'

/**
 * 幸运数字那段代码 —— **只在「运行」时执行**。
 *
 * ⚠️ 别写成模板字符串：这段代码会被**原样存进文档**（`code` 那一行），
 * 反引号在里面只会让读的人以为编辑器支持插值。用字符串拼接最不容易误读。
 */
const LUCKY_CODE = [
  "// 只在「运行」时执行：掷一个 1–9 的幸运数字（设计期不跑用户代码）",
  'const n = Math.floor(Math.random() * 9) + 1',
  "return { lucky: '幸运数字 ' + n }",
].join('\n')

/**
 * JS 节点的输入声明：**一个输入都没有**。
 *
 * ⚠️ 不是偷懒：它是"掷骰子"，没有东西要喂给它。空声明 + 代码里的
 * `return` 就是它的全部接口（输出端口由 `deriveOutputPorts` 从代码推）。
 */
const LUCKY_PORTS: JsPortsConfig = { inputs: [] }

/* ══════════════════════════════════════════════════════════════════
   造节点的小工具（示例专用 —— 只有十几行，不值得往内核里加东西）
   ══════════════════════════════════════════════════════════════════ */

/**
 * 示例能用的节点定义 = **打印包自己的 + 内核的**（`const.text` / `core.js`）。
 *
 * ⚠️ 用列表查而不是 `switch`：多一个类型只要往数组里加一份定义，
 * 不用回来改第二个地方。
 */
const DEFS: readonly NodeTypeDef[] = [...PRINT_NODE_TYPES, ...CONST_NODE_TYPES, JS_NODE_TYPE]

function defOf(type: string): NodeTypeDef {
  const def = DEFS.find((item) => item.type === type)
  /*
   * ⚠️ 找不到**当场抛**：示例是一份写死的图，类型名打错的表现是
   * 「点开示例什么也没有」—— 那是打开的时候才发现的事，
   * 而 `verify:print` 会在校验期就把它红出来（拿每个类型去查合并目录）。
   */
  if (!def) throw new Error(`示例文档用了一个目录里没有的节点类型：${type}`)
  return def
}

/**
 * 把一个值写进某一行的控件。
 *
 * ⚠️ 行不存在 / 行没有控件 → 抛（理由同 `defOf`）。
 * 静默跳过的后果更隐蔽：**节点在、控件是空的**，看着只像"示例忘了填"。
 */
function setRow(rows: NodeRow[], rowId: string, value: unknown): void {
  const row = rows.find((item) => item.id === rowId)
  if (!row || (row.kind !== 'property' && row.kind !== 'input') || !row.widget) {
    throw new Error(`示例文档：这个节点上没有带控件的行「${rowId}」`)
  }
  ;(row.widget as { value: unknown }).value = value
}

/**
 * 造一个节点模型 —— 形状和 `makeModelOfType`（宿主建节点走的那条）**一致**，
 * 只是数据来自上面那份定义表。
 *
 * ⚠️ `rows` 走 `cloneRows`（**深拷贝**）：见文件头 ①。
 */
function node(
  type: string,
  id: string,
  values: Record<string, unknown> = {},
  label?: string,
): GraphNodeModel {
  const def = defOf(type)
  const rows = cloneRows(def.rows)
  for (const [rowId, value] of Object.entries(values)) setRow(rows, rowId, value)
  return {
    id,
    type: def.type,
    title: def.title,
    category: def.category,
    width: def.width,
    height: def.height,
    render: def.render,
    editorOnly: def.editorOnly,
    /* ⚠️ `label` 只在给了的时候写：它的语义是"用户自己起的名字"，
     * 写一个 undefined 进去会让"没起名"和"起过名又删了"分不开 */
    ...(label ? { label } : {}),
    rows,
  }
}

/**
 * 造一个 JS 节点。
 *
 * ⚠️ 它的行**必须由 `rowsForJsNode(ports, code)` 生成**，不能从 `def.rows` 拷：
 * `def.rows` 是"默认那两个输入 + 默认代码"的那一份，而**端口是声明 + 代码里
 * 的 `return` 推出来的**（见 `jsNodeType.ts` 文件头）。照抄默认行的表现是
 * 「端口和代码对不上」——接不上去，或者接上了不算。
 *
 * ⚠️ 外面再套 `cloneRows`：`rowsForJsNode` 本身就是纯工厂（每次都是新对象），
 * 但和别的节点走同一条"实例必须自己一份"的路更不容易出事 ——
 * 哪天它改成复用一份常量行，这里不用跟着改。
 */
function jsNode(
  id: string,
  ports: JsPortsConfig,
  code: string,
  label?: string,
): GraphNodeModel {
  const def = JS_NODE_TYPE
  return {
    id,
    type: def.type,
    title: def.title,
    category: def.category,
    width: def.width,
    height: def.height,
    render: def.render,
    editorOnly: def.editorOnly,
    ...(label ? { label } : {}),
    rows: cloneRows(rowsForJsNode(ports, code)),
  }
}

/**
 * 子图里的一条边。
 *
 * ⚠️ **没有 `id`**：子图的边本来就不带 id（见 `SubGraphEdge`）——
 * 它是序列化成文档那一刻才起名的（`graph/serialization.ts` 的
 * `edgeIdOfEndpoints`）。在这里编一个 id 等于把文档格式抄第二遍。
 */
function edge(
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
): SubGraphEdge {
  return { source, sourceHandle, target, targetHandle }
}

/* ══════════════════════════════════════════════════════════════════
   ★ 那张标签
   ══════════════════════════════════════════════════════════════════ */

/**
 * 幸运饼干签：**文档 → 页面 → 六个组件**。
 *
 * ## 排版（mm，纸 60×40、边距 2）
 *
 * ```text
 *   ┌──────────────────────────────────────────┐ 边框 2,2 → 58,38
 *   │            幸运饼干 · 今日运势             │ 标题 3.5,3.5 高 6.5 居中
 *   │ ──────────────────────────────────────── │ 分隔线 10.8（填充的矩形，不是线）
 *   │ 今天会遇到一件小事，            ┌────────┐ │ 祝福语 12.2 起（两行）
 *   │ 正好是你一直想做的。            │  二维码 │ │ 二维码 41,19.1（内容 = 同一句祝福）
 *   │ 幸运数字 7                      └────────┘ │ 幸运数字 25.5 起
 *   └──────────────────────────────────────────┘
 * ```
 *
 * ⚠️ 组件之间**不许压在一起**（`verify:print` 按毫米两两查过）：
 *   · 两个文字块、一个二维码在 x 上错开（36.5 / 38.5 / 41 各有一道界线）；
 *   · 边框那一个**故意包住所有组件** —— 它是外框，不是"叠在上面"。
 *     所以校验的规则是「要么不相交、要么一个完全包住另一个」。
 *
 * ⚠️ 二维码写的是 `wMm: 16`（**目标宽度**），而它画出来的边长是
 * "模块数 × 整数点"算出来的（见 `pageModel.ts` 那段"框是框"）：
 * 那句祝福 → 33×33 模块 + 静区 = 41 模块 → 203dpi 下 3 点/模块 = **15.39mm**。
 * 所以实际边长只会**小于等于** 16mm：位置是按 15.39 对齐的（右边齐标题、
 * 下边齐幸运数字），而按 16 去查重叠是**保守**的、安全的。
 */
function fortuneLabel(): SubGraph {
  /* ── 内容源（左列） ─────────────────────────────────────────── */

  /* 「一句祝福」：它是这张标签**唯一的内容真源**（文本和二维码都读它） */
  const source = node('const.text', 'fortune', { value: FORTUNE }, '内容源 · 那句祝福')
  /* 「幸运数字」：设计期空值、运行期掷骰子（见 `LUCKY_CODE`） */
  const luckyNumber = jsNode('lucky', LUCKY_PORTS, LUCKY_CODE, '运行期 · 掷幸运数字')

  /* ── 纸上的六个组件 ─────────────────────────────────────────── */

  /* 边框：包住整张标签（圆角 2mm，所以它看着像张"签"而不是个方框） */
  const frame = node(
    BOX_NODE_TYPE,
    'frame',
    { xMm: 2, yMm: 2, wMm: 56, hMm: 36, lineWidthMm: 0.4, radiusMm: 2 },
    '边框',
  )
  const title = node(
    TEXT_NODE_TYPE,
    'title',
    {
      xMm: 3.5,
      yMm: 3.5,
      wMm: 53,
      hMm: 6.5,
      text: '幸运饼干 · 今日运势',
      sizeMm: 4,
      bold: true,
      align: 'center',
      vAlign: 'middle',
    },
    '标题',
  )
  /*
   * 分隔线：**矩形**而不是"一条线" —— 组件里没有线这个种类，
   * 而"填充的扁矩形"就是一条线（线宽 0 + 填充 = 实心黑条）。
   */
  const rule = node(
    BOX_NODE_TYPE,
    'rule',
    {
      xMm: 3.5,
      yMm: 10.8,
      wMm: 53,
      hMm: 0.6,
      lineWidthMm: 0,
      fill: true,
      fillColor: '#000000',
    },
    '分隔线',
  )
  /* 祝福语：内容**接线**（来自 `const.text`），内联值只是没接线时的兜底 */
  const fortuneText = node(
    TEXT_NODE_TYPE,
    'fortune-text',
    {
      xMm: 3.5,
      yMm: 12.2,
      wMm: 35,
      hMm: 12,
      text: FORTUNE,
      sizeMm: 3.2,
      align: 'left',
      vAlign: 'top',
    },
    '祝福语',
  )
  /* 幸运数字：内联值是**设计期占位**，连线之后由 JS 节点在运行期覆盖它 */
  const luckyText = node(
    TEXT_NODE_TYPE,
    'lucky-text',
    {
      xMm: 3.5,
      yMm: 25.5,
      wMm: 33,
      hMm: 9,
      text: LUCKY_PLACEHOLDER,
      sizeMm: 4.5,
      bold: true,
      align: 'left',
      vAlign: 'middle',
    },
    '幸运数字',
  )
  /* 二维码：内容与祝福语**同源**（同一句话，扫出来就是它） */
  const qr = node(
    QRCODE_NODE_TYPE,
    'qr',
    {
      /*
       * ⚠️ 位置是按**实际边长 15.39mm** 对齐的（右边齐标题、下边齐幸运数字），
       * 而参数写的是目标宽度 16 —— 实际只会更小，见函数头那段说明。
       *
       * ⚠️ 2027 补一句：这条二维码也有 0.3mm 的框（`lineWidthMm` 的默认值），
       * 而**框不许压静区** —— 所以它的**组件框**比图形大一圈（15.39 → 15.89，
       * 两侧各让出半个毫米的框内缩，见 `barcodeElementOf` 的 `insetMm`）。
       * 图形本身的位置没变（还是按 15.39 对齐），大出来的那半毫米是框的占地。
       */
      xMm: 41,
      yMm: 19.1,
      wMm: 16,
      hMm: 16,
      text: FORTUNE,
      symbology: 'qrcode',
      /*
       * ⚠️ X 维度**只在"宽 = 0（自动）"时**才决定模块宽 —— 这里写了目标宽度，
       * 所以模块宽是 16 ÷ 41 取整到 **3 个点**算出来的（3 点 = 375µm @203dpi，
       * 正是 `verify:print` 条码那一节验过的合格值：偏差 0、没有警告）。
       * 留着 375 是为了"哪天把宽改回 0（自动）"时仍然落在好值上。
       */
      xDimMicron: 375,
      ecLevel: 'M',
    },
    '二维码',
  )

  /* ── 收口：页面 → 文档 ───────────────────────────────────────── */

  /* 页面：只有"装了哪些组件"这一件事（纸张在文档上） */
  const page = node(PAGE_NODE_TYPE, 'page')
  /* 文档：纸张（60×40）+ 作业（份数 / 切刀）—— 这是"一份活"的根 */
  const document = node(DOCUMENT_NODE_TYPE, 'doc', {
    widthMm: 60,
    heightMm: 40,
    dpi: 203,
    marginMm: 2,
    copies: 1,
    cut: 'after-each',
  })

  return {
    /*
     * 顺序 = 画布上的摆放顺序（和连线无关）。内容源在前、组件在中、
     * 页面与文档在后 —— 和数据的流向一致，读图时不用来回跳。
     */
    nodes: [source, luckyNumber, frame, title, rule, fortuneText, luckyText, qr, page, document],
    /*
     * ⚠️ handle 全部是**真实的端口 id**（拼错一个的表现是"示例少了一个组件"：
     * `items.N` 里那个空槽被丢掉，而页面上什么提示都没有）。
     *   · 组件输出行 = `out`（`COMPONENT_OUT`）、页面输出行 = `out`；
     *   · 页面收组件是变参行 `items`（句柄 = `${rowId}.${i}`）；
     *   · 文档收页面是变参行 `pages`；
     *   · JS 节点的输出按**位置**发：`out0`。
     */
    edges: [
      edge('fortune', 'out', 'fortune-text', 'text'),
      edge('fortune', 'out', 'qr', 'text'),
      edge('lucky', 'out0', 'lucky-text', 'text'),
      /*
       * 层序 = `items.N` 的序号（不是数组顺序）：边框最先画（最底），
       * 其余按"从上到下"的顺序叠 —— 它们本来就不重叠，所以只是习惯。
       */
      edge('frame', 'out', 'page', 'items.0'),
      edge('title', 'out', 'page', 'items.1'),
      edge('rule', 'out', 'page', 'items.2'),
      edge('fortune-text', 'out', 'page', 'items.3'),
      edge('lucky-text', 'out', 'page', 'items.4'),
      edge('qr', 'out', 'page', 'items.5'),
      edge('page', 'out', 'doc', 'pages.0'),
    ],
    /*
     * 画布坐标（px，只有设计器看它）。
     *
     * ⚠️ 分四列排：内容源 → 组件 → 页面 → 文档，和数据的流向一致。
     * 同一列里上下留够 200px 以上 —— 普通节点的高度按行数自适应
     * （打印组件铺开有三四百像素），贴太近就是叠在一起。
     */
    positions: {
      fortune: { x: 0, y: 40 },
      lucky: { x: 0, y: 300 },
      frame: { x: 360, y: -160 },
      title: { x: 360, y: 60 },
      rule: { x: 360, y: 280 },
      'fortune-text': { x: 700, y: -160 },
      'lucky-text': { x: 700, y: 120 },
      qr: { x: 700, y: 320 },
      page: { x: 1040, y: 40 },
      doc: { x: 1360, y: 40 },
    },
  }
}

/**
 * 打印包带的示例文档清单（**今天一份**）。
 *
 * ⚠️ `id` / `uuid` 都是**稳定标识**：前者给菜单用（`node/package.ts` 的
 * 注册表按它查重），后者写进文档库 —— 改了它，用户库里那份示例会**变成两份**
 * （旧的那份还在，新的一份又进来）。`verify:print` 把 uuid 钉成了字面量。
 */
export const PRINT_EXAMPLES: PackageExample[] = [
  {
    id: 'print.example.fortune',
    /* 名字里带「示例」：它同时是**收进文档库之后的文档名**，得让人认得出 */
    name: '示例 · 幸运饼干签',
    hint: '一张 60×40mm 的标签：一句祝福同时喂给文本和二维码，幸运数字由 JS 节点在「运行」时掷出',
    uuid: 'example-1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d',
    graph: fortuneLabel,
  },
]
